import { ApplicationController } from '@aha-app/mvc';
import { type DragDropManager } from 'dnd-core';
import { type IEvent } from 'fabric/fabric-impl';
import { generateJitteredKeyBetween } from 'fractional-indexing-jittered';
import { type ObjectChangedData } from '../external-types';
import type Canvas from '../objects/Canvas';
import type Frame from '../objects/Frame';
import { hasFrameMemberBehaviors, isFrame } from '../objects/guards';
import { type WhiteboardDisplayMode } from '../shared-types';
import ToolFrameBase from '../tools/ToolFrameBase';
import { type Rect } from '../types';
import trackEvent from '../utils/analytics';
import { determineZoomVpt } from '../utils/zoom';
import type WhiteboardController from './WhiteboardController';
import { type DocData } from '../collaboration/Serialization';

const THUMBNAIL_CACHE_MAX_DEPTH = 20;
// the width of the thumbnails within `--side-panel--menu-width` minus paddings
const FRAME_THUMBNAIL_WIDTH = 582;

interface Thumbnail {
  src: string;
}

interface FrameState {
  id: string;
  sortKey: string | undefined;
}

export interface PresentationFrame extends FrameState {
  getName: () => string;
  getPosition: () => Rect;
  getThumbnail: () => Thumbnail | undefined;
  getVisibility: () => boolean;

  frame: Frame;
}

interface State {
  frames?: FrameState[];
  currentFrameId?: string;
  framesVisible: boolean;
  selectedFrameId?: string;

  canvasWidth: number;
  canvasHeight: number;
}
export interface PresentationControllerProps {
  mode?: WhiteboardDisplayMode;
  setIsPresenting?: (p: boolean) => void;
  dragDropManager?: DragDropManager;
}

const sortKeyCmp = (a: string | undefined, b: string | undefined) => {
  if (a === b) {
    return 0;
  }
  if (a === undefined) {
    return 1;
  }
  if (b === undefined) {
    return -1;
  }

  return a > b ? 1 : -1;
};

const frameCmp = (a: FrameState, b: FrameState) =>
  sortKeyCmp(a.sortKey, b.sortKey);

class PresentationController extends ApplicationController<
  State,
  PresentationControllerProps
> {
  static initialState: State = {
    canvasWidth: 100,
    canvasHeight: 100,
    framesVisible: false,
  };

  canvas: Canvas;
  whiteboardController: WhiteboardController;
  cleanup?: () => void;

  startController(canvas: Canvas, whiteboardController: WhiteboardController) {
    this.canvas = canvas;
    this.whiteboardController = whiteboardController;

    const pinCurrentFrame = () => {
      if (this.isPresenting()) {
        this.navigateToCurrentFrame();
      }
    };

    const callback = ({ target }: { target?: fabric.Object }) => {
      if (target && isFrame(target)) {
        // rebuild frame list
        this.syncFrames();
        this.clearThumbnailCache([target.id]);

        // TODO on initial load this clashes with the WhiteboardController.documentDidLoad
        // method which also tries to set the zoom - we need a unified approach here, which
        // also accounts for fullScreen and the desired zoom when exiting presentation mode
        pinCurrentFrame();
      }

      // clear thumbnail cache for any related frames
      if (target && hasFrameMemberBehaviors(target) && target.container) {
        this.clearThumbnailCache([target.container]);
      }
    };

    // https://github.com/fabricjs/fabric.js/wiki/Working-with-events
    canvas.on('object:added', callback);
    canvas.on('object:removed', callback);

    // clear thumbnail cache for any relevant object changes
    const onObjectChanged = ({ changedFrameIds }: ObjectChangedData) => {
      if (changedFrameIds.size) {
        this.clearThumbnailCache(changedFrameIds);
      }
    };
    canvas.on('object:changed', onObjectChanged);

    const setCanvasWidth = () => {
      this.state.canvasWidth = canvas.getWidth();
      this.state.canvasHeight = canvas.getHeight();
    };
    setCanvasWidth();

    // pin canvas to the current frame, if presenting
    const resizeObserver = new ResizeObserver(() => {
      setCanvasWidth();
      pinCurrentFrame();
    });

    resizeObserver.observe(canvas.getElement());

    this.cleanup = () => {
      resizeObserver.disconnect();
      canvas.off('object:added', callback);
      canvas.off('object:removed', callback);
      canvas.off('object:changed', onObjectChanged);
    };
  }

  destroy() {
    this.cleanup?.();
  }

  private previousZoom: number[] | undefined = undefined;

  changeProps(
    newProps: PresentationControllerProps,
    oldProps: PresentationControllerProps
  ): void {
    if (newProps.mode === 'presentation' && oldProps.mode !== 'presentation') {
      this.previousZoom = this.canvas.viewportTransform;

      // Navigate to the selected frame at presentation start if there is one
      if (this.state.selectedFrameId) {
        this.state.currentFrameId = this.state.selectedFrameId;
      }

      this.navigateToCurrentFrame();
      this.whiteboardController.actionSetStylus(
        this.whiteboardController.stylusPointer
      );
    } else if (
      newProps.mode !== 'presentation' &&
      oldProps.mode === 'presentation'
    ) {
      this.state.framesVisible = false;

      if (this.previousZoom) {
        this.canvas.setViewportTransform(this.previousZoom);
        this.canvas.fire('zoom');
      }
    }
  }

  // use to turn on listening for whiteboard changes when rendering a component that needs
  // to update reactively based on whiteboard data
  // note we always clear the thumbnail cache on change (see listeners in initialize)
  // because that's cheap and is needed to avoid a stale cache
  public updateOnChange() {
    const onObjectChanged = ({ changedFrameIds }: ObjectChangedData) => {
      if (changedFrameIds.size) {
        this.syncFrames();
      }
    };
    this.canvas.on('object:changed', onObjectChanged);

    const onObjectRemoved = (e: IEvent) => {
      if (e.target && hasFrameMemberBehaviors(e.target) && e.target.container) {
        this.syncFrames();
      }
    };
    this.canvas.on('object:removed', onObjectRemoved);

    // ensure the frame list is up to date for the initial render
    this.syncFrames();

    return () => {
      this.canvas.off('object:changed', onObjectChanged);
      this.canvas.off('object:removed', onObjectRemoved);
    };
  }

  private readonly frameCache = new Map<string, PresentationFrame>();

  public getFrame(id: string): PresentationFrame | undefined {
    if (!this.frameCache.get(id)) {
      const frame = ToolFrameBase.findFrame(this.canvas, id);
      if (frame) {
        this.frameCache.set(id, {
          id,
          sortKey: frame.sortKey,
          getName: () => frame.plainText(),
          getPosition: () => frame.absolutePosition(),
          getThumbnail: () => this.getFrameThumbnail(frame.id),
          getVisibility: () => !frame.notInNavigation,
          frame,
        });
      }
    }

    return this.frameCache.get(id);
  }

  private updateSortKey(frameIndex: number, newSortKey: string | undefined) {
    const frame = this.state.frames?.[frameIndex];
    if (!frame) {
      return;
    }

    frame.sortKey = newSortKey;
    const frameObject = this.getFrame(frame.id)?.frame;
    if (frameObject) {
      frameObject.set('sortKey', newSortKey);
    }
  }

  public toggleFrameVisibility(frameId: string, visible: boolean) {
    const frame = this.getFrame(frameId);
    if (frame) {
      frame.frame.set('notInNavigation', !visible);
    }

    this.syncFrames();
  }

  public resetFrameOrder() {
    const frames = this.state.frames;
    if (!frames) {
      return;
    }

    for (let frameIndex = 0; frameIndex < frames.length; frameIndex++) {
      this.updateSortKey(frameIndex, undefined);
    }

    this.syncFrames();

    trackEvent(
      'Feature-7298498475865992680',
      'User resets presentation frame order'
    );
  }

  public moveFrame(dragIndex: number, hoverIndex: number) {
    if (!this.state.frames) {
      console.warn('Frames not synced');
      return;
    }

    const frames = this.state.frames;

    // ensure all frames have a sortKey set before reordering. we don't do this sooner because we want
    // the order to be determined by position on the canvas until explicitly reordered by the user
    let prev: string | undefined;
    for (let frameIndex = 0; frameIndex < frames.length; frameIndex++) {
      const frame = frames[frameIndex];
      // if the frame has no sortKey, or its sortKey is out of sync, reset it
      if (!frame.sortKey || (prev && sortKeyCmp(prev, frame.sortKey) === 1)) {
        this.updateSortKey(
          frameIndex,
          generateJitteredKeyBetween(prev ?? null, null)
        );
      }
      prev = frame.sortKey;
    }

    const insertAfter = dragIndex > hoverIndex ? hoverIndex - 1 : hoverIndex;

    let newSortKey: string;
    try {
      newSortKey = generateJitteredKeyBetween(
        frames[insertAfter]?.sortKey ?? null,
        frames[insertAfter + 1]?.sortKey ?? null
      );
    } catch (err) {
      if (err instanceof Error) {
        const firstFrame = frames[insertAfter];
        const secondFrame = frames[insertAfter + 1];
        err.message += `firstFrame === secondFrame: ${
          firstFrame === secondFrame
        }. firstFrame.id = ${firstFrame?.id} secondFrame.id = ${
          secondFrame?.id
        }`;
      }
      throw err;
    }
    this.updateSortKey(dragIndex, newSortKey);

    frames.sort(frameCmp);

    trackEvent(
      'Feature-7298498475865992680',
      'User reorders presentation frame order'
    );
  }

  public hasOrderedFrames() {
    return this.state.frames?.some(frame => frame.sortKey) ?? false;
  }

  getFrames(includeHidden = false): PresentationFrame[] {
    return (this.state.frames ?? []).reduce<PresentationFrame[]>(
      (frames, { id }) => {
        const frame = this.getFrame(id);

        if (frame && (includeHidden || frame.getVisibility())) {
          frames.push(frame);
        }
        return frames;
      },
      []
    );
  }

  /** Ensures that any frames in the pasted data don't cause a duplicate
   * sortKey error.
   *
   * Data in the clipboard might contain frames, and if the frames have
   * been manually ordered by the user then they will contain sortKeys
   * that can be duplicates of the keys we already have (more likely if
   * copy-pasting in the same document). Ensure pasted frames don't cause
   * a key conflict, and also try to maintain their sort-order, if they have one,
   * in case a user is merging presentations from two different whiteboards.
   */
  prepareFramesForPaste(docData: DocData<any>) {
    const nonFrameObjects: typeof docData.objects = {};
    const frameObjects: typeof docData.objects = {};

    Object.entries(docData.objects).forEach(([key, value]) => {
      if (value.type === 'frame') {
        frameObjects[key] = value;
      } else {
        nonFrameObjects[key] = value;
      }
    });

    /*
    We have the following scenarios, where "manually ordered" means "has sortKey"
    1. Target whiteboard manually ordered, pasted frames manually ordered
    2. Target whiteboard manually ordered, pasted frames not manually ordered
    3. Target whiteboard not manually ordered, pasted frames manually ordered
    4. Target whiteboard not manually ordered, pasted frames not manually ordered

    Sort the incoming frames with the presentation comparator function, to try
    to put them in the same order they had in the source whiteboard if the user had
    manually ordered them (at a minimum the ones that had been manually ordered will
    maintain their order while frames with undefined sortKeys might change their order, but
    only relative to other undefined frames in the pasted set).

    We can then put the sorted frames at the "end" of the objects we're pasting,
    in the sense that docData.objects will be traversed in chronological order of when keys
    were added (Ecmascript spec guarantees this), and frame objects are being added
    in sorted order.

    Then need to delete the sortKey so that there can't be duplicate sortKey errors. Also
    if the user hasn't applied manual ordering to the target whiteboard we don't
    want to make it manually ordered by inserting frames with sortKeys.

    Deleting the sortKey works in all scenarios because the frame list can have a mix
    of defined and undefined sortKeys, and undefined comes last in the presentation
    order - unless they're all undefined in which case whiteboard-position ordering
    is applied. */
    const framesInOrder = Object.entries(frameObjects).sort((frame1, frame2) =>
      // Variables are [objectId, objectData]
      frameCmp(frame1[1], frame2[1])
    );
    framesInOrder.forEach(idFramePair => {
      delete idFramePair[1].sortKey;
    });
    const newObjectData: typeof docData.objects = {
      ...nonFrameObjects,
      ...Object.fromEntries(framesInOrder),
    };
    docData.objects = newObjectData;
  }

  actionNavigateToFrame(frameId: string) {
    this.state.currentFrameId = frameId;
    this.state.framesVisible = false;
    this.navigateToCurrentFrame();
  }

  actionNavigate(to: 'next' | 'previous') {
    const frames = this.getFrames();

    if (!frames.length) {
      return;
    }

    const currentIndex = this.currentFrameIndex();

    const frameIndex = Math.max(
      0,
      Math.min(frames.length - 1, currentIndex + (to === 'next' ? 1 : -1))
    );

    this.actionNavigateToFrame(frames[frameIndex].id);
  }

  actionStartPresenting() {
    if (this.canPresent() && this.props.setIsPresenting) {
      this.props.setIsPresenting(true);
      trackEvent(
        'Feature-7298498475865992680',
        'User starts presentation mode'
      );
    }
  }

  actionStopPresenting() {
    this.props.setIsPresenting?.(false);
  }

  actionToggleFrames(visible?: boolean) {
    this.state.framesVisible = visible ?? !this.state.framesVisible;
  }

  actionSetSelectedFrame(frameId: string) {
    this.state.selectedFrameId = frameId;
  }

  isPresenting() {
    return this.props.mode === 'presentation';
  }

  framesVisible() {
    return this.isPresenting() && this.state.framesVisible && this.state.frames;
  }

  canPresent() {
    return !!this.getFrames().length;
  }

  hasFrames() {
    return !!this.state.frames?.length;
  }

  matteDimensions():
    | {
        type: 'x' | 'y';
        proportion: number;
      }
    | undefined {
    const frame = this.currentFrame();

    if (!frame) {
      return;
    }

    const { canvasWidth, canvasHeight } = this.state;
    const { width, height } = frame.getPosition();

    return width / height > canvasWidth / canvasHeight
      ? {
          type: 'y',
          proportion:
            (canvasHeight - (height * canvasWidth) / width) / 2 / canvasHeight,
        }
      : {
          type: 'x',
          proportion:
            (canvasWidth - (width * canvasHeight) / height) / 2 / canvasWidth,
        };
  }

  currentFrameIndex() {
    const current = this.currentFrameId();
    return this.getFrames().findIndex(frame => frame.id === current) ?? 0;
  }

  private currentFrameId() {
    const { currentFrameId } = this.state;
    const frames = this.getFrames();
    return (frames.find(({ id }) => currentFrameId === id) ?? frames[0])?.id;
  }

  private currentFrame() {
    const id = this.currentFrameId();
    return id ? this.getFrame(id) : undefined;
  }

  private navigateToCurrentFrame() {
    const frame = this.currentFrame();

    if (!frame || !this.canvas) {
      return;
    }

    const newVpt = determineZoomVpt(
      {
        width: this.state.canvasWidth,
        height: this.state.canvasHeight,
      },
      frame.getPosition(),
      { marginFactor: 1 }
    );
    if (this.canvas.viewportTransform?.toString() !== newVpt.toString()) {
      this.whiteboardController.actionSetViewport(newVpt);
    }
  }

  public syncFrames() {
    const frameObjects = ToolFrameBase.orderedFrames(this.canvas);
    const frames = frameObjects.map(frame => {
      return {
        id: frame.uniqueId(),
        sortKey: frame.sortKey,
      };
    });

    frames.sort(frameCmp);

    this.state.frames = frames;
  }

  private thumbnailCache?: Map<string, Thumbnail>;

  public getFrameThumbnail(frameId: string) {
    if (!this.thumbnailCache) {
      this.thumbnailCache = new Map();
    }

    if (!this.thumbnailCache.get(frameId)) {
      const frameArea = this.getFrame(frameId)?.getPosition();

      if (frameArea) {
        const result = this.canvas.renderToPng(frameArea, {
          width: FRAME_THUMBNAIL_WIDTH,
        });

        if (!result.success) {
          console.warn(
            `Could not render frame thumbnail. Error: ${result.error}`
          );
        } else {
          this.thumbnailCache.set(frameId, { src: result.dataUrl });
        }
      }
    }

    return this.thumbnailCache.get(frameId);
  }

  public clearThumbnailCache(frameIds?: string[] | Set<string>, depth = 0) {
    // if we haven't generated any thumbnails yet, there's nothing to clear
    if (!this.thumbnailCache) return;

    // guard in case we have circular container reference in the data
    if (depth > THUMBNAIL_CACHE_MAX_DEPTH) return;

    if (!frameIds) {
      this.thumbnailCache.clear();
      return;
    }

    for (const frameId of frameIds) {
      this.thumbnailCache.delete(frameId);

      // also clear any parent frames, as their thumbnails will also change
      const frame = this.getFrame(frameId);
      if (frame?.frame.container) {
        this.clearThumbnailCache([frame.frame.container], ++depth);
      }
    }
  }
}

export default PresentationController;
